/**
 * @fileoverview Business event requirements validation and Excel import.
 * @module logic/businessEvents/businessEventsRequirements
 */

import { parseExcelFile } from "../../utils/common.js";
import {
  getAuthJsonHeaders,
  logDevError,
  resolveExpiredToken,
} from "../../utils/eventApi.js";
import { validateRequirementsExcelData } from "../../utils/excelValidation.js";

/**
 * Fetches requirements for validation from a participating company.
 * Each requirement carries the requesting user's company data (datosEmpresaUsuario)
 * together with the missing field maps computed against that same company snapshot.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {string} participatingCompanyId - Participating company identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array|null>} Requirements, or null when the session expired and the user was redirected
 */
export const fetchValidateRequirements = async (
  eventId,
  participatingCompanyId,
  navigate,
) => {
  const userId = localStorage.getItem("userId");
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompany/${participatingCompanyId}/${userId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      fetchValidateRequirements(eventId, participatingCompanyId, navigate),
    );
    if (tokenResult) {
      return tokenResult.redirected ? null : (tokenResult.result ?? []);
    }
    return data.participatingCompany?.requerimientos || [];
  } catch (error) {
    logDevError(error);
    return [];
  }
};

/**
 * Fetches requirements for validation from a participating company into state.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {string} participatingCompanyId - Participating company identifier
 * @param {Function} setRequirements - State setter for requirements
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const getValidateRequirements = async (
  eventId,
  participatingCompanyId,
  setRequirements,
  navigate,
) => {
  const requirements = await fetchValidateRequirements(
    eventId,
    participatingCompanyId,
    navigate,
  );
  if (requirements === null) {
    return;
  }
  setRequirements(requirements);
};

/**
 * Processes an Excel file to import requirements in bulk.
 * @async
 * @param {File} file - Excel file to process
 * @param {Object} validationFunctions - Validation helper functions
 * @param {Function} t - Translation function
 * @returns {Promise<Object>} Result with success flag, results/errors arrays
 */
export const processRequirementsExcelFile = async (
  file,
  validationFunctions,
  t,
) => {
  try {
    const excelData = await parseExcelFile(file);
    const validation = validateRequirementsExcelData(
      excelData,
      validationFunctions,
      t,
    );

    if (!validation.isValid) {
      return {
        success: false,
        error: validation.error,
        invalidRows: validation.invalidRows || [],
      };
    }

    if (validation.invalidRows && validation.invalidRows.length > 0) {
      return {
        success: false,
        error: validation.error,
        invalidRows: validation.invalidRows,
      };
    }

    // Return the valid requirements for processing
    return {
      success: true,
      results: validation.validRequirements,
      errors: [],
    };
  } catch (error) {
    logDevError("Error processing Excel file:", error);
    return {
      success: false,
      error: t("pages.buyerCompanyForm.excelProcessingError"),
    };
  }
};
